import { tracking } from '@navio/observability';

import { commonTrackingEvents } from './events';
import { pickDefined } from './pickDefined';
import { clearClaim, isClaimed, markClaimed, sessionOnceKey } from './sessionOnce';

const BOOKING_FLOW_STARTED_KEY = sessionOnceKey('booking-flow-started');
const OPENPANEL_ONLY = ['openpanel'] as const;

// Last shipped `bookingFlowContext` payload — every step page remounts the
// caller, so only a real change of plates / department / flow type sends.
const lastBookingFlowContext: { key?: string } = {};

export type BookingFlowContext = {
  brand?: string;
  locationType?: 'mobile' | 'stationary';
  garageId?: number;
  garageName?: string;
  serviceDepartmentId?: number;
};

// Brand / locationType stay as session super-props. Garage identity is only on
// the started event so mobile→stationary switches do not leave stale garage tags.
function registerBookingFlowContext(props: BookingFlowContext) {
  const extra = pickDefined({
    brand: props.brand,
    locationType: props.locationType
  });
  if (Object.keys(extra).length > 0) {
    tracking.register(extra);
  }
}

// Once per analytics session until a booking is created or logout
// (`tracking.clear`) resets the claim. First wizard steps (mobile address,
// stationary car) own this so deep links still start the funnel.
export function trackBookingFlowStarted(props: BookingFlowContext) {
  registerBookingFlowContext(props);
  if (isClaimed(BOOKING_FLOW_STARTED_KEY)) {
    return;
  }
  tracking.track(
    commonTrackingEvents.bookingFlowStarted,
    pickDefined({
      brand: props.brand,
      locationType: props.locationType,
      garageId: props.garageId,
      garageName: props.garageName,
      serviceDepartmentId: props.serviceDepartmentId
    })
  );
  markClaimed(BOOKING_FLOW_STARTED_KEY);
}

export function resetBookingFunnel() {
  clearClaim(BOOKING_FLOW_STARTED_KEY);
  lastBookingFlowContext.key = undefined;
}

type BookingFlowSearchContext = {
  locationType: 'mobile' | 'stationary';
  licensePlates: string[];
  serviceDepartmentName?: string;
};

// Lets support find a customer's OpenPanel session + replay by plate, service
// department or flow type, including flows abandoned before a booking exists.
// OpenPanel-only: plates must not reach Faro or Mixpanel. Plates are joined into
// one string so OpenPanel's `contains` filter matches one plate among several.
export function trackBookingFlowContext({
  locationType,
  licensePlates,
  serviceDepartmentName
}: BookingFlowSearchContext) {
  if (licensePlates.length === 0 && !serviceDepartmentName) {
    return;
  }
  const props = pickDefined({
    locationType,
    licensePlates: licensePlates.length > 0 ? licensePlates.join(', ') : undefined,
    serviceDepartmentName
  });
  const key = JSON.stringify(props);
  if (key === lastBookingFlowContext.key) {
    return;
  }
  lastBookingFlowContext.key = key;
  tracking.track(commonTrackingEvents.bookingFlowContext, props, { only: OPENPANEL_ONLY });
}
